import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { getConfig } from 'src/global-config';

import { SplashScreen } from 'src/components/loading-screen/splash-screen';

import { AuthGuard } from 'src/auth/guard/auth-guard';
import { HRAuthGuard } from 'src/auth/guard/hr-guard';
import { RoleGuard } from 'src/auth/guard/role-guard';

import { JobNavData, DashboardLayout, LayoutCreateJob } from './lazy-layouts';

// ----------------------------------------------------------------------

const JobsPage = lazy(() => import('src/pages/jobs/container/job'));
const JobEditor = lazy(() => import('src/pages/jobs/container/jobEditor'));
const CandidatesPage = lazy(() => import('src/pages/jobs/container/candidates/candidateMain'));
const CreateJobPage = lazy(() => import('src/pages/jobs/organisms/createJob'));
const JobForms = lazy(() => import('src/pages/jobs/organisms/forms'));
const JobWorkflows = lazy(() => import('src/pages/jobs/container/job-workflow/jobWorkflow'));
const InterviewHub = lazy(() => import('src/pages/jobs/organisms/interview-hub'));
const InterviewAgenda = lazy(() => import('src/pages/jobs/organisms/interview-agenda'));
const InterviewEvaluation = lazy(() => import('src/pages/jobs/organisms/interview-evaluation'));
const Compare = lazy(() => import('src/pages/jobs/organisms/compare'));
const JobSettings = lazy(() => import('src/pages/jobs/organisms/job-settings'));
const JobReport = lazy(() => import('src/pages/jobs/organisms/job-report'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  return (
    <Suspense fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const createJobLayout = () => (
  <LayoutCreateJob>
    <SuspenseOutlet />
  </LayoutCreateJob>
);

// Every job page is reachable by every role except where backend/docs/rbac-plan.md §4.1 says otherwise;
// those pages sit behind a RoleGuard so a Hiring Team member following a link sees the permission page
// rather than a screen of 403 toasts. Job details and candidates are read by everyone.
export function getJobsRoutes(): RouteObject[] {
  const CONFIG = getConfig();

  return [
  {
    path: 'jobs',
    element: CONFIG.auth.skip ? (
      dashboardLayout()
    ) : (
      <AuthGuard>
        <HRAuthGuard>{dashboardLayout()}</HRAuthGuard>
      </AuthGuard>
    ),
    children: [
      { element: <JobsPage />, index: true },
      {
        element: (
          <JobNavData>
            <Outlet />
          </JobNavData>
        ),
        children: [
          {
            path: 'job-details',
            element: <JobEditor />,
          },
          {
            path: 'candidates',
            element: <CandidatesPage />,
          },
          {
            path: 'forms',
            element: (
              <RoleGuard action="jobs.forms.view">
                <JobForms />
              </RoleGuard>
            ),
          },
          {
            path: 'workflow',
            element: (
              <RoleGuard action="jobs.workflow.view">
                <JobWorkflows />
              </RoleGuard>
            ),
          },
          {
            path: 'interview-hub',
            element: (
              <RoleGuard action="jobs.interviewHub.view">
                <InterviewHub />
              </RoleGuard>
            ),
          },
          {
            path: 'interview-agenda',
            element: (
              <RoleGuard action="jobs.workflow.view">
                <InterviewAgenda />
              </RoleGuard>
            ),
          },
          {
            path: 'interview-evaluation',
            element: (
              <RoleGuard action="jobs.evaluation.view">
                <InterviewEvaluation />
              </RoleGuard>
            ),
          },
          {
            path: 'compare',
            element: (
              <RoleGuard action="jobs.compare">
                <Compare />
              </RoleGuard>
            ),
          },
          {
            path: 'job-settings',
            element: (
              <RoleGuard action="jobs.settings">
                <JobSettings />
              </RoleGuard>
            ),
          },
          {
            path: 'job-report',
            element: (
              <RoleGuard action="jobs.report">
                <JobReport />
              </RoleGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'jobs/create-job',
    element: CONFIG.auth.skip ? (
      createJobLayout()
    ) : (
      <AuthGuard>
        <HRAuthGuard>{createJobLayout()}</HRAuthGuard>
      </AuthGuard>
    ),
    children: [
      {
        element: (
          <RoleGuard action="jobs.create">
            <CreateJobPage />
          </RoleGuard>
        ),
        index: true,
      },
    ],
    }
  ];
}
